import type { DeliveryAssignmentEntity } from '../location.types';
import type { VDriveStatus } from '../shared.types';
import type { PendingBankSummary } from './outstandingBalance.types';

export type SendReportVDRiveRequestData = {
  emailList: Array<string>;
  from: string;
  to: string;
};
/**
 * `isVDriveCard` is still a required field on the backend DTO, but `true` is
 * refused ("The vGrubs vDrive card can no longer be assigned") since the
 * vGrubs vDrive card started being retired - the owner's own card is the only
 * option, so the type only lets `false` through.
 */
export type StartLocationVerificationProcessVDriveRequestData = {
  stripeToken: string;
  isVDriveCard: false;
  /** Money up front, at least $25; required only with `paymentMode: topUp`. */
  amount?: number;
  autoRechargeAmount: number;
  autoRechargeWhenAmount: number;
  /** Left out, the backend picks `perDelivery`. */
  paymentMode?: VDrivePaymentMode;
};

/**
 * How a vDrive balance is paid for. `perDelivery` (the default for new
 * balances): each delivery charges the card only for what the balance is
 * missing, at least $1.00, and credit on the balance is spent first.
 * `topUp`: the card adds `autoRechargeAmount` whenever the balance drops below
 * `autoRechargeWhenAmount`. The auto-recharge amounts are kept in both, so a
 * switch to top-up has them.
 */
export enum VDrivePaymentMode {
  perDelivery = 'perDelivery',
  topUp = 'topUp',
}

/**
 * `PUT /v{1,2}/vdrive/:locationId/payment-mode` (admin and support, or the
 * owner). Changes the whole balance, so every location sharing it. The amounts
 * are optional (the saved ones are used), but a switch to `topUp` with none
 * saved is a 400, so send both when picking top-up.
 */
export type UpdateVDrivePaymentModeRequest = {
  paymentMode: VDrivePaymentMode;
  autoRechargeAmount?: number;
  autoRechargeWhenAmount?: number;
};

/**
 * What a switch to top-up would save in card fees, from the last `days` of
 * the balance's own card charges. Cents throughout.
 */
export type VDriveCardFeeSavings = {
  days: number;
  /** Card charges in the period. */
  cardCharges: number;
  /** What those charges added to the balance, fees left out. */
  chargedAmount: number;
  /** The card fees paid on them. */
  cardFees: number;
  /** What the same amount would have cost in fees as `topUpAmount` top-ups. */
  topUpCardFees: number;
  /** `cardFees - topUpCardFees`, never below 0. */
  savings: number;
  /** The top-up compared with ($50). */
  topUpAmount: number;
  /** The period's average delivery, or $10 without deliveries. */
  exampleDeliveryAmount: number;
  /** The fee when that delivery is charged on its own. */
  exampleDeliveryCardFee: number;
  /** The share of a top-up's fee that falls on that delivery. */
  exampleTopUpCardFee: number;
  exampleSavingsPerDelivery: number;
};

export type ActivateVDriveV2Dto = {
  stripeToken: string;
  // address?: {
  //   zipCode: string;
  //   address: string;
  //   streetNumber: string;
  //   city: string;
  //   state: string;
  //   country: string;
  // };
  /** Money up front, at least $25; not needed paying per delivery. */
  amount?: number;
  autoRechargeAmount: number;
  autoRechargeWhenAmount: number;
  /** Left out, the backend picks `perDelivery`. */
  paymentMode?: VDrivePaymentMode;
  locationIds?: Array<number>;
};

/** See `StartLocationVerificationProcessVDriveRequestData` for `isVDriveCard`. */
export type UpdateVDriveCreditCardRequestData = {
  stripeToken: string;
  isVDriveCard: false;
  address?: {
    zipCode: string;
    address: string;
    streetNumber: string;
    city: string;
    state: string;
    country: string;
  };
};

/**
 * `PUT /v1/vdrive/location/:locationId/secondary-card` (admin, support): the
 * same Stripe token and optional billing address as the primary card, without
 * `isVDriveCard` - the card number never reaches the backend.
 */
export type SetVDriveSecondaryCardRequest = Omit<
  UpdateVDriveCreditCardRequestData,
  'isVDriveCard'
>;

export type UpdateCreditCardRequestData = {
  number: string;
  cvc: string;
  expMonth: string;
  expYear: string;
};

export type InitiateVDriveVerificationProcessForPartnerRequestData = {
  number: string;
  cvc: string;
  expMonth: string;
  expYear: string;
  amount: number;
  autoRechargeAmount: number;
  autoRechargeWhenAmount: number;
};

export type ActivateVDriveRequestData = {
  enableVgrubsBrands: boolean;
  autoAssignDriver: boolean;
  achCustomerId?: string | null;
  vDriveFee?: number | null;
  vGrubsFee?: number | null;
};

export enum VDrivePreferredAssignmentType {
  cheapest = 'cheapest',
  fastest = 'fastest',
}

/**
 * The merchant's view of a location's vDrive (`GET /v2/vdrive/:locationId` on
 * the customer API), also what the pay-with-card route answers with.
 * `availableBalance` goes negative while the location is in overdraft.
 */
export type GetVDriveResponse = {
  autoRechargeAmount: number;
  autoRechargeWhenAmount: number;
  createdAt: string;
  freeDeliveries: number;
  status: VDriveStatus;
  /**
   * Why an admin rejected it; set only while `status` is `rejected`. Read it
   * per location here: `GET /v1/vdrive` says `inactive` for a rejected one.
   */
  rejectionReason?: string | null;
  availableBalance: number;
  autoAssign: boolean;
  cardFailed: boolean;
  last4?: string | null;
  /** The secondary card; null when there is none. */
  secondaryLast4?: string | null;
  isVDriveCard: boolean;
  /** Bank payments in progress - "$200.00 on its way by bank". */
  pendingBank: PendingBankSummary;
  /** Undefined from a backend from before Oct 27, 2026 (top-up only then). */
  paymentMode?: VDrivePaymentMode;
  /**
   * How far the balance may go negative when the card fails, in cents:
   * $100 / $200 / $300 by reserve level, or a limit vGrubs set. 0 without a
   * balance.
   */
  reserveLimit?: number;
  /**
   * The reserve the owner can unlock next by paying on time and using vDrive;
   * null at the top level or with a limit set by vGrubs. What it takes is
   * internal and not shown.
   */
  nextReserveLimit?: number | null;
  /** Null without a balance. */
  cardFeeSavings?: VDriveCardFeeSavings | null;
};

/**
 * `PUT /v1/vdrive/:locationId/overdraft-limit` (admin). Cents; `null` goes
 * back to the automatic limit of the balance's reserve level ($100 / $200 /
 * $300). A set limit is left alone by the daily level-up. The backend caps it
 * at 1,000,000.
 */
export type UpdateVDriveOverdraftLimitRequest = {
  overdraftLimit: number | null;
};

/**
 * A card from the merchant app, as a Stripe token plus the billing address -
 * the body of `location-card` (primary) and `secondary-card` on the customer
 * API. The card number never reaches the backend.
 */
export type UpdateCreditCardV2Dto = {
  stripeToken: string;
  /**
   * False saves the card without charging it for a negative balance (a $1.00
   * authorization still checks it). Left out it is true: charged right away.
   * Ignored by a backend from before Oct 2, 2026, which always charges.
   */
  chargeNow?: boolean;
  address?: {
    zipCode: string;
    address: string;
    streetNumber: string;
    city: string;
    state: string;
    country: string;
  };
};

/**
 * `POST /v1/vdrive/{location|brand}/:id/strategy-suggestion`. Free text the
 * AI reads as context ("~20% of weekend deliveries run late"), up to 1000
 * characters - longer is a 400.
 */
export type VDriveStrategySuggestionRequest = {
  note?: string | null;
};

export type VDriveStrategySuggestionScope = 'location' | 'brand';

/**
 * The parts of the strategy screen the AI answers for, one each, always all
 * seven and in display order.
 */
export type VDriveStrategySectionKey =
  | 'providers'
  | 'times'
  | 'deliveryNowReassign'
  | 'bannedProviders'
  | 'preferredAssignmentType'
  | 'nonOperatingConditions'
  | 'ignoreGlobalStrategies';

export type VDriveStrategySection = {
  key: VDriveStrategySectionKey;
  /** Ready to show; the same heading the screen uses. */
  title: string;
  /** The AI wants this part changed. */
  changed: boolean;
  /**
   * The change passes the same validation as saving the strategy. False
   * means it must not be applied (and is always false when nothing changed).
   */
  applicable: boolean;
  /** Why a changed section is not applicable. */
  errors: string[];
  /** 2-4 sentences with numbers; present for unchanged sections too. */
  reasoning: string;
  /** One sentence; empty when nothing changed. */
  expectedImpact: string;
  /**
   * AI estimates for applying this section alone; null = no estimate.
   * Percentage points, negative = fewer late deliveries.
   */
  estimatedLateRateChange: number | null;
  /** Minutes, negative = faster. */
  estimatedMedianMinutesChange: number | null;
  /** Cents per delivery, positive = more expensive. */
  estimatedCostChangePerDeliveryCents: number | null;
  confidence: 'high' | 'medium' | 'low';
  /** Only this section's own fields, as they are now. */
  current: Partial<DeliveryAssignmentEntity>;
  /** Only this section's own fields, as suggested. */
  suggested: Partial<DeliveryAssignmentEntity>;
};

export type VDriveStrategySuggestion = {
  scope: VDriveStrategySuggestionScope;
  locationId: number;
  brandId: number | null;
  /** The location's or the brand's name. */
  name: string;
  /**
   * The brand has no strategy of its own, so `current` is the location's and
   * applying creates the brand's own.
   */
  inheritedFromLocation: boolean;
  data: {
    /** ISO, the 90-day window. */
    from: string;
    to: string;
    /** Delivery attempts, cancelled ones included. */
    attempts: number;
    completed: number;
    /** Too little data: the suggestion is to be taken with care. */
    lowVolume: boolean;
    /** A brand with few deliveries: the whole location's were added. */
    includesLocationDeliveries: boolean;
    /** Other vDrive locations nearby, added when this one has few. */
    nearbyLocations: {
      locationId: number;
      name: string;
      miles: number;
      completed: number;
    }[];
  };
  /** How it goes now, for the panel header. Rates are percentages. */
  baseline: {
    completedPerDay: number;
    /** Late by more than 20 min past the promised time; null with no promises. */
    lateRate: number | null;
    /** Share of deliveries that had a promised time (lateRate is measured on these). */
    promiseCoverage: number;
    /** Over 60 min from order to handoff. */
    slowRate: number | null;
    medianTotalMinutes: number | null;
    p90TotalMinutes: number | null;
    /** Average fleet price per delivery, cents. */
    avgProviderFeeCents: number | null;
    /** Orders that needed more than one delivery attempt. */
    reassignShare: number;
  };
  /** 2-3 sentences: the panel's main text. */
  summary: string;
  sections: VDriveStrategySection[];
  /** The strategy now; null when there is none. */
  current: DeliveryAssignmentEntity | null;
  /** `current` with every applicable section applied. */
  suggested: DeliveryAssignmentEntity;
  risks: string[];
  /** Things the team must know (little data, a broken current rule, ...). Always shown. */
  warnings: string[];
};

/**
 * The AI works for 1-3 minutes, longer than Cloudflare holds a request, so
 * the POST starts a job and the GET is polled for it. Both answer this.
 * A job and its result live for an hour, then the GET is a 404.
 */
export type VDriveStrategySuggestionJob = {
  jobId: string;
  status: 'pending' | 'done' | 'failed';
  scope: VDriveStrategySuggestionScope;
  locationId: number;
  brandId: number | null;
  startedAt: string;
  finishedAt: string | null;
  /** The sentence for the admin when the job failed. */
  error: string | null;
  /** Set once the job is done. */
  result: VDriveStrategySuggestion | null;
};

/** A vDrive fleet that takes vGrubs orders, from `GET /v1/vdrive/fleet-organizations`. */
export type VDriveFleetOrganization = {
  /** Fleet code, e.g. "VD-10012" - what a location or brand is pinned to. */
  id: string;
  name: string;
  /** A restaurant with its own drivers, or a delivery company. */
  type: 'restaurant' | 'fleet';
  /** `own` delivers only its own restaurant's orders, `open` any order. */
  deliveryScope: VDriveFleetDeliveryScope;
  address: string | null;
  addressParts: {
    streetNumber: string | null;
    street: string;
    unitNumber: string | null;
    city: string;
    state: string | null;
    zipCode: string;
    country: string;
  } | null;
  lat: number | null;
  lng: number | null;
  /** Dispatch phone. */
  phoneNumber: string | null;
  /** `false` = no price list yet, so the fleet gives no quotes. */
  quotable: boolean;
  /** When the owner switched vGrubs on. */
  linkedAt: string | null;
};

export type VDriveFleetDeliveryScope = 'own' | 'open';

export type VDriveFleetOrganizationsParams = {
  deliveryScope?: VDriveFleetDeliveryScope;
  query?: string;
};

/**
 * How a location's pinned fleet stands in vDrive right now. The pin is kept
 * when the owner switches vGrubs off, and works again once it is back on.
 */
export type LocationFleetOrganizationStatus = {
  organizationId: string | null;
  organizationName: string | null;
  /** `false` = switched vGrubs off or deleted, `null` = not pinned. */
  linked: boolean | null;
  /** `null` when not pinned or not linked. */
  organization: VDriveFleetOrganization | null;
};
